import { AzureAvatarConfig } from "@models/avatar-config";
import { AzureAvatarHelper } from "@utils/azureAvatarHelper";
import { AzureAvatarTokenService } from "@service/avatar/azure-avatar-token.service";
import {
  AvatarSynthesizer,
  CancellationDetails,
  CancellationReason,
  SpeechSynthesisResult,
} from "microsoft-cognitiveservices-speech-sdk";
import sleep from "@utils/sleep";
import { ResultReason } from "microsoft-cognitiveservices-speech-sdk/distrib/lib/src/sdk/Exports";
import { RetryUtil } from "@utils/retry.util";

let queue: AzureAvatarService[] = [];

export class AzureAvatarService {
  private avatar: AvatarSynthesizer | null = null;
  private tokenService: AzureAvatarTokenService | null = null;
  private videoConnected = false;
  private audioConnected = false;

  public running = false;
  public stopping = false;
  public error = false;

  get connected(): boolean {
    return this.running && this.videoConnected && this.audioConnected;
  }

  async start(
    avatarConfig: AzureAvatarConfig,
    video: HTMLVideoElement,
    audio: HTMLAudioElement,
    sessionId: string,
  ): Promise<void> {
    queue.push(this);

    while (queue[0] !== this) {
      await sleep(500);
    }

    await this.init(avatarConfig, video, audio, sessionId);
  }

  async speak(text: string): Promise<void> {
    if (!this.avatar || !this.running || this.stopping) {
      return;
    }
    await this.avatar.speakTextAsync(text);
  }

  async stop(): Promise<void> {
    this.stopping = true;
    while (!this.running && !this.error) {
      await sleep(500);
    }
    await this.destroy();
    queue = queue.slice(1);
    this.stopping = false;

    window.removeEventListener("beforeunload", this.forceDestroy);
  }

  private async forceDestroy() {
    await this.destroy(false);
  }

  private async init(
    avatarConfig: AzureAvatarConfig,
    video: HTMLVideoElement,
    audio: HTMLAudioElement,
    sessionId: string,
  ): Promise<void> {
    const peerConnection = AzureAvatarHelper.createWebRTCConnection(
      avatarConfig,
      (event) => {
        if (event.track.kind === "video") {
          video.id = event.track.kind;
          video.srcObject = event.streams[0];
          this.videoConnected = true;
        } else {
          audio.srcObject = event.streams[0];
          this.audioConnected = true;
        }
      },
    );

    this.avatar = AzureAvatarHelper.createAvatarSynthesizer(avatarConfig);

    await RetryUtil.retry({
      asyncFunc: () => this.startAvatar(this.avatar!, peerConnection),
      retries: 3,
      interval: 100,
      shouldRetry: (error: any) => {
        console.log(`Checking error ${error}`);
        return (
          typeof error === "object" &&
          error.toString &&
          error.toString().includes("1011")
        );
      },
    });

    this.running = true;
    this.tokenService = new AzureAvatarTokenService(
      this.avatar,
      sessionId,
    ).start();

    window.addEventListener("beforeunload", this.forceDestroy);
  }

  private async startAvatar(
    avatar: AvatarSynthesizer,
    peerConnection: RTCPeerConnection,
  ): Promise<void> {
    const r = await avatar.startAvatarAsync(peerConnection);

    if (r.reason === ResultReason.SynthesizingAudioCompleted) {
      console.log("Avatar started. Result ID: " + r.resultId);
      this.error = false;
    } else {
      console.log("Unable to start avatar. Result ID: " + r.resultId);
      if (r.reason === ResultReason.Canceled) {
        let cancellationDetails = CancellationDetails.fromResult(
          r as SpeechSynthesisResult,
        );
        if (cancellationDetails.reason === CancellationReason.Error) {
          console.log(cancellationDetails.errorDetails);
        }
        console.log(
          "Unable to start avatar: " + cancellationDetails.errorDetails,
        );
      }
      this.error = true;
      throw new Error(r.errorDetails);
    }
  }

  private async destroy(graceful = true): Promise<void> {
    try {
      if (this.avatar) {
        if (graceful) {
          await this.avatar.stopSpeakingAsync();
        }
        await this.avatar.close();
      }

      if (this.tokenService) {
        this.tokenService.stop();
      }
      this.running = false;
    } catch (e) {
      console.error(e);
    }
  }
}
